import { useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface FaceProps {
  expression?: 'smile' | 'sad' | 'shock' | 'surprise' | 'neutral' | 'hurt' | 'celebrate';
  eyeColor?: string;
  mouthColor?: string;
  glow?: boolean;
  scale?: number;
}

export function Face({ expression = 'smile', eyeColor = '#000000', mouthColor = '#ffffff', glow = false, scale = 1 }: FaceProps) {
  const leftEye = useRef<THREE.Mesh>(null);
  const rightEye = useRef<THREE.Mesh>(null);
  const mouth = useRef<THREE.Mesh>(null);
  const [blink, setBlink] = useState(1);
  const nextBlink = useRef(0);

  useFrame((state) => {
    const t = state.clock.elapsedTime;

    // Random natural blinking (2-6 second intervals)
    if (t > nextBlink.current) {
      setBlink(0.1);
      nextBlink.current = t + 0.1 + Math.random() * 0.1;
      setTimeout(() => {
        setBlink(1);
        nextBlink.current = t + 2 + Math.random() * 4;
      }, 150);
    }

    if (leftEye.current && rightEye.current) {
      const targetScaleY = expression === 'hurt' ? 0.3 : blink;
      const targetScaleX = expression === 'surprise' || expression === 'shock' ? 1.5 : 1;

      leftEye.current.scale.y = THREE.MathUtils.lerp(leftEye.current.scale.y, targetScaleY, 0.3);
      rightEye.current.scale.y = THREE.MathUtils.lerp(rightEye.current.scale.y, targetScaleY, 0.3);

      leftEye.current.scale.x = THREE.MathUtils.lerp(leftEye.current.scale.x, targetScaleX, 0.3);
      rightEye.current.scale.x = THREE.MathUtils.lerp(rightEye.current.scale.x, targetScaleX, 0.3);
    }

    if (mouth.current) {
      if (expression === 'smile' || expression === 'sad' || expression === 'celebrate') {
        mouth.current.scale.y = 1 + Math.sin(t * 8) * 0.2;
      } else if (expression === 'shock' || expression === 'surprise') {
        mouth.current.scale.setScalar(1 + Math.sin(t * 12) * 0.15);
      } else if (expression === 'hurt') {
        mouth.current.scale.y = 0.5 + Math.sin(t * 20) * 0.1;
      }
    }
  });

  let mouthGeo;
  let mouthPos: [number, number, number] = [0, -0.15 * scale, 0.01];
  let mouthRot: [number, number, number] = [0, 0, 0];

  if (expression === 'smile' || expression === 'celebrate') {
    mouthGeo = <torusGeometry args={[0.1 * scale, 0.03 * scale, 8, 16, Math.PI]} />;
    mouthPos = [0, -0.1 * scale, 0.01];
    mouthRot = [0, 0, Math.PI];
  } else if (expression === 'sad') {
    mouthGeo = <torusGeometry args={[0.1 * scale, 0.03 * scale, 8, 16, Math.PI]} />;
    mouthPos = [0, -0.2 * scale, 0.01];
  } else if (expression === 'shock' || expression === 'surprise') {
    mouthGeo = <circleGeometry args={[0.08 * scale, 16]} />;
  } else if (expression === 'hurt') {
    mouthGeo = <planeGeometry args={[0.15 * scale, 0.02 * scale]} />;
  } else {
    mouthGeo = <planeGeometry args={[0.1 * scale, 0.03 * scale]} />;
  }

  return (
    <group position={[0, 0, 0.5 * scale]}>
      <mesh ref={leftEye} position={[-0.2 * scale, 0.1 * scale, 0.01]}>
        <planeGeometry args={[0.08 * scale, 0.15 * scale]} />
        <meshStandardMaterial
          color={eyeColor}
          emissive={glow ? eyeColor : '#000'}
          emissiveIntensity={glow ? 2 : 0}
        />
      </mesh>
      <mesh ref={rightEye} position={[0.2 * scale, 0.1 * scale, 0.01]}>
        <planeGeometry args={[0.08 * scale, 0.15 * scale]} />
        <meshStandardMaterial
          color={eyeColor}
          emissive={glow ? eyeColor : '#000'}
          emissiveIntensity={glow ? 2 : 0}
        />
      </mesh>
      <mesh ref={mouth} position={mouthPos} rotation={mouthRot}>
        {mouthGeo}
        <meshStandardMaterial color={mouthColor} />
      </mesh>
    </group>
  );
}
